import { useCallback, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  apiGetValidationSchema,
  type FieldRule,
  type ItemPath,
  type MarketFieldRule,
} from 'api/quote-marketplace';
import {
  ValidationSchemaContext,
  getNestedValue,
  hasValue,
  evaluateRequired,
  validateFormat,
} from './useValidationSchema';

interface FieldValidationError {
  field: string;
  message: string;
  tab: string;
  section: string;
}

interface ValidationSchemaProviderProps {
  quoteId: number;
  children: ReactNode;
}

export const ValidationSchemaProvider = ({
  quoteId,
  children,
}: ValidationSchemaProviderProps) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['quote-marketplace', 'validation-schema', quoteId],
    queryFn: () => apiGetValidationSchema(quoteId),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });

  const getFieldRule = useCallback(
    (itemPath: ItemPath, fieldPath: string): FieldRule | undefined => {
      return data?.fields?.[itemPath]?.[fieldPath];
    },
    [data],
  );

  const isFieldRequired = useCallback(
    (
      itemPath: ItemPath,
      fieldPath: string,
      entity: Record<string, unknown>,
    ): boolean => {
      const rule = data?.fields?.[itemPath]?.[fieldPath];
      if (!rule) return false;
      return evaluateRequired(rule.required, entity);
    },
    [data],
  );

  const isFieldVisible = useCallback(
    (
      itemPath: ItemPath,
      fieldPath: string,
      entity: Record<string, unknown>,
    ): boolean => {
      const rule = data?.fields?.[itemPath]?.[fieldPath];
      if (!rule?.visible_when) return true;

      const { field, operator, values } = rule.visible_when;
      const actualValue = getNestedValue(entity, field);

      if (operator === 'in') {
        if (Array.isArray(actualValue)) {
          return actualValue.some((v) => values.includes(String(v)));
        }
        return values.includes(String(actualValue));
      }

      if (operator === 'not_in') {
        if (Array.isArray(actualValue)) {
          return !actualValue.some((v) => values.includes(String(v)));
        }
        return !values.includes(String(actualValue));
      }

      return true;
    },
    [data],
  );

  const validateField = useCallback(
    (
      itemPath: ItemPath,
      fieldPath: string,
      entity: Record<string, unknown>,
    ): string | null => {
      const rule = data?.fields?.[itemPath]?.[fieldPath];
      if (!rule) return null;

      const value = getNestedValue(entity, fieldPath);
      const isRequired = evaluateRequired(rule.required, entity);

      if (isRequired && !hasValue(value)) {
        if (typeof rule.required === 'object' && rule.required.message) {
          return rule.required.message;
        }
        return rule.message;
      }

      if (hasValue(value)) {
        if (rule.format && !validateFormat(value, rule.format)) {
          return rule.format_message ?? `Invalid format`;
        }

        if (
          rule.min_length !== undefined &&
          typeof value === 'string' &&
          value.trim().length < rule.min_length
        ) {
          return rule.message;
        }

        if (rule.min_value !== undefined) {
          const numValue =
            typeof value === 'string' ? parseFloat(value) : (value as number);
          if (!isNaN(numValue) && numValue < rule.min_value) {
            return rule.message;
          }
        }
      }

      return null;
    },
    [data],
  );

  const validateEntity = useCallback(
    (
      itemPath: ItemPath,
      entity: Record<string, unknown>,
    ): FieldValidationError[] => {
      const rules = data?.fields?.[itemPath];
      if (!rules) return [];

      const errors: FieldValidationError[] = [];

      for (const [fieldPath, rule] of Object.entries(rules)) {
        const errorMessage = validateField(itemPath, fieldPath, entity);
        if (errorMessage) {
          errors.push({
            field: fieldPath,
            message: errorMessage,
            tab: rule.tab,
            section: rule.section,
          });
        }
      }

      return errors;
    },
    [data, validateField],
  );

  const getMarketFieldRule = useCallback(
    (itemPath: ItemPath, fieldPath: string): MarketFieldRule | undefined => {
      return data?.market_fields?.[itemPath]?.[fieldPath];
    },
    [data],
  );

  const isFieldRequiredByMarket = useCallback(
    (itemPath: ItemPath, fieldPath: string): boolean => {
      const marketRule = data?.market_fields?.[itemPath]?.[fieldPath];
      return !!marketRule && marketRule.required_by.length > 0;
    },
    [data],
  );

  const getMarketsRequiringField = useCallback(
    (itemPath: ItemPath, fieldPath: string): string[] | null => {
      const marketRule = data?.market_fields?.[itemPath]?.[fieldPath];
      if (!marketRule || marketRule.display_names.length === 0) return null;
      return marketRule.display_names;
    },
    [data],
  );

  const value = {
    schema: data,
    isLoading,
    error,
    isFieldRequired,
    isFieldVisible,
    validateField,
    validateEntity,
    getFieldRule,
    getMarketFieldRule,
    isFieldRequiredByMarket,
    getMarketsRequiringField,
  };

  return (
    <ValidationSchemaContext.Provider value={value}>
      {children}
    </ValidationSchemaContext.Provider>
  );
};
